import { authService } from '../service/auth/auth-service'
import { logger } from '../util/logger'
import { toasterUtil } from '../util/toaster-util'
import { queryKeys } from '../util/query-keys'
import { useMutation } from '@tanstack/react-query'

const APPLE_SIGN_IN_ERROR_MESSAGE = 'Apple Sign In failed. Please try again or contact support if the problem persists.'

type SignInWithAppleInput = {
  identityToken: string
  redirectUrl?: string
}

type AppleSuccessResponse = { authorization?: { id_token?: string } }

export const useSignInWithApple = (
  redirectUrl?: string,
): {
  handleSuccess: (response: AppleSuccessResponse) => void
  handleError: (err?: unknown) => void
  isLoading: boolean
} => {
  const { mutate: signInWithAppleMutation, isLoading } = useMutation<void, Error, SignInWithAppleInput>(
    async (input) => {
      await authService.signInWithApple({
        identityToken: input.identityToken,
        redirectUrl: input.redirectUrl ?? redirectUrl,
      })
    },
    {
      mutationKey: queryKeys.signInWithApple(),
      onMutate: () => authService.signupStarted('apple'),
      onError: (err) => {
        logger.error('Apple Sign In error', { error: err })
        toasterUtil.error(err.message ?? APPLE_SIGN_IN_ERROR_MESSAGE)
      },
    },
  )

  const handleSuccess = (response: AppleSuccessResponse): void => {
    const identityToken = response.authorization?.id_token
    if (!identityToken) {
      logger.error('Identity token not found in Apple response', { response })
      toasterUtil.error(APPLE_SIGN_IN_ERROR_MESSAGE)
      return
    }
    signInWithAppleMutation({ identityToken, redirectUrl })
  }

  const handleError = (err?: unknown): void => {
    logger.error('Apple Sign In error', { err })
    if (err && typeof err === 'object' && (err as { error?: string }).error === 'popup_closed_by_user') {
      return
    }
    toasterUtil.error(APPLE_SIGN_IN_ERROR_MESSAGE)
  }

  return {
    handleSuccess,
    handleError,
    isLoading,
  }
}
